import { API, API_BASE } from '../../config/api';
import { useState, useRef, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Sparkles, Copy, Tag, Trash2, StopCircle, Calendar, Percent, DollarSign, Check } from 'lucide-react';


export function PromoGenerator({ tracks, beats, merchItems, onRefresh }) {
  const [promos, setPromos] = useState([]);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  const [form, setForm] = useState({
    promo_name: '',
    discount_code: '',
    discount_type: 'percentage',
    discount_value: '',
    start_date: '',
    end_date: '',
    items: [],
    promo_text: ''
  });

  useEffect(() => {
    fetchPromos();
  }, []);

  const fetchPromos = async () => {
    try {
      const { data } = await api.get(`${API}/promos`, { withCredentials: true });
      setPromos(data);
    } catch {}
  };

  const addItem = (type, item) => {
    const exists = form.items.find(i => i.id === item.id && i.type === type);
    if (exists) return;
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { type, id: item.id, name: item.title || item.name, price: item.price || 0, sale_price: '', cover: item.cover_image || '' }]
    }));
  };

  const removeItem = (index) => {
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const updateItemSalePrice = (index, price) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, sale_price: price } : item)
    }));
  };

  const generatePromoText = async () => {
    setGenerating(true);
    try {
      const itemsDesc = form.items.map(i => `${i.name} (${i.type})`).join(', ');
      const discountInfo = form.discount_code
        ? `${form.discount_value}${form.discount_type === 'percentage' ? '%' : '$'} off with code ${form.discount_code}`
        : 'Sale prices active';
      const { data } = await api.post(`${API}/promos/generate-text`, {
        promo_name: form.promo_name || 'Sale',
        items_description: itemsDesc,
        discount_info: discountInfo
      }, { withCredentials: true });
      setForm(prev => ({ ...prev, promo_text: data.generated_text }));
      toast.success('Promo text generated!');
    } catch {
      toast.error('Failed to generate text');
    }
    setGenerating(false);
  };

  const generatePromoImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1080;

    // Background
    ctx.fillStyle = '#0A0A0A';
    ctx.fillRect(0, 0, 1080, 1080);

    // Red accent bar
    ctx.fillStyle = '#FF3B30';
    ctx.fillRect(0, 0, 1080, 8);
    ctx.fillRect(0, 1072, 1080, 8);

    // Gold diagonal accent
    ctx.fillStyle = '#DAA520';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(120, 0);
    ctx.lineTo(0, 120);
    ctx.fill();

    // Promo name
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 72px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(form.promo_name || 'SALE', 540, 180);

    // Discount info
    if (form.discount_code) {
      ctx.fillStyle = '#FF3B30';
      ctx.font = 'bold 96px Arial, sans-serif';
      const discText = form.discount_type === 'percentage'
        ? `${form.discount_value}% OFF`
        : `$${form.discount_value} OFF`;
      ctx.fillText(discText, 540, 340);

      ctx.fillStyle = '#DAA520';
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.fillText(`CODE: ${form.discount_code.toUpperCase()}`, 540, 420);
    }

    // Items list
    ctx.fillStyle = '#FFFFFF';
    ctx.font = '32px Arial, sans-serif';
    const startY = form.discount_code ? 520 : 360;
    form.items.slice(0, 6).forEach((item, i) => {
      const y = startY + (i * 60);
      ctx.fillStyle = '#A3A3A3';
      ctx.fillText(item.name, 540, y);
      if (item.sale_price) {
        ctx.fillStyle = '#FF3B30';
        ctx.font = '28px Arial, sans-serif';
        ctx.fillText(`$${item.price} → $${item.sale_price}`, 540, y + 35);
        ctx.font = '32px Arial, sans-serif';
      }
    });

    // Date range
    if (form.start_date || form.end_date) {
      ctx.fillStyle = '#666666';
      ctx.font = '24px Arial, sans-serif';
      const dateText = form.end_date ? `Ends ${new Date(form.end_date).toLocaleDateString()}` : '';
      ctx.fillText(dateText, 540, 980);
    }

    // Bottom branding
    ctx.fillStyle = '#DAA520';
    ctx.font = 'bold 28px Arial, sans-serif';
    ctx.fillText('LINK IN BIO', 540, 1040);
  };

  const downloadPromoImage = () => {
    generatePromoImage();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `${form.promo_name || 'promo'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Promo image downloaded!');
  };

  const copyPromoText = () => {
    navigator.clipboard.writeText(form.promo_text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!form.promo_name.trim()) { toast.error('Promo name required'); return; }
    setSaving(true);
    try {
      await api.post(`${API}/promos`, {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        items: form.items.map(i => ({ type: i.type, id: i.id, name: i.name, sale_price: parseFloat(i.sale_price) || null, cover: i.cover }))
      }, { withCredentials: true });
      toast.success('Promo launched! Sale prices applied.');
      setCreating(false);
      setForm({ promo_name: '', discount_code: '', discount_type: 'percentage', discount_value: '', start_date: '', end_date: '', items: [], promo_text: '' });
      fetchPromos();
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save promo');
    }
    setSaving(false);
  };

  const endPromo = async (promoId) => {
    if (!window.confirm('End this promo? Sale prices will be removed from all items.')) return;
    try {
      await api.post(`${API}/promos/${promoId}/end`, {}, { withCredentials: true });
      toast.success('Promo ended!');
      fetchPromos();
      onRefresh();
    } catch { toast.error('Failed to end promo'); }
  };

  const deletePromo = async (promoId) => {
    if (!window.confirm('Delete this promo record?')) return;
    try {
      await api.delete(`${API}/promos/${promoId}`, { withCredentials: true });
      toast.success('Deleted!');
      fetchPromos();
    } catch { toast.error('Failed to delete'); }
  };

  const allItems = [
    ...tracks.filter(t => t.is_downloadable && t.price > 0).map(t => ({ ...t, _type: 'track' })),
    ...beats.filter(b => b.sale_status !== 'not_for_sale').map(b => ({ ...b, _type: 'beat', price: b.price_basic || 0 })),
    ...merchItems.filter(m => m.price > 0).map(m => ({ ...m, _type: 'merch', title: m.name }))
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-2xl font-black uppercase">Promo Generator</h2>
          <p className="text-[#A3A3A3] text-sm mt-1">Create sales, generate promo text & images</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none" data-testid="create-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> New Promo
        </Button>
      </div>

      {/* Active & Past Promos */}
      {promos.length > 0 && (
        <div className="space-y-3">
          {promos.map(promo => (
            <div key={promo.id} className={`bg-[#141414] border p-4 flex items-center gap-4 ${promo.status === 'active' ? 'border-[#FF3B30]' : 'border-[#262626]'}`} data-testid={`promo-${promo.id}`}>
              <div className={`w-2 h-full min-h-[40px] flex-shrink-0 ${promo.status === 'active' ? 'bg-[#FF3B30]' : 'bg-[#333]'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold uppercase truncate">{promo.promo_name}</h4>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${promo.status === 'active' ? 'bg-[#FF3B30] text-white' : 'bg-[#333] text-[#666]'}`}>
                    {promo.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-[#666] mt-1">
                  {promo.discount_code && <span className="text-[#DAA520]"><Tag className="w-3 h-3 inline" /> {promo.discount_code}</span>}
                  <span>{(promo.items || []).length} items</span>
                  {promo.end_date && <span>Ends {new Date(promo.end_date).toLocaleDateString()}</span>}
                </div>
              </div>
              {promo.status === 'active' && (
                <Button onClick={() => endPromo(promo.id)} variant="outline" size="sm" className="rounded-none border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white" data-testid={`end-promo-${promo.id}`}>
                  <StopCircle className="w-4 h-4 mr-1" /> End
                </Button>
              )}
              <Button onClick={() => deletePromo(promo.id)} variant="outline" size="sm" className="rounded-none border-[#262626] text-[#666]" data-testid={`delete-promo-${promo.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {promos.length === 0 && !creating && (
        <div className="bg-[#141414] border border-[#262626] p-12 text-center">
          <Tag className="w-12 h-12 mx-auto text-[#262626] mb-4" />
          <p className="text-[#A3A3A3]">No promos yet. Create your first one!</p>
        </div>
      )}

      {/* Create Promo Dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-[#141414] border-[#262626] rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cabinet font-bold uppercase text-xl">Create Promo</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Promo Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Promo Name *</Label>
                <Input value={form.promo_name} onChange={(e) => setForm(prev => ({ ...prev, promo_name: e.target.value }))}
                  className="bg-[#0A0A0A] border-[#262626] rounded-none" placeholder="Summer Sale" data-testid="promo-name" />
              </div>
              <div>
                <Label>Discount Code (optional)</Label>
                <Input value={form.discount_code} onChange={(e) => setForm(prev => ({ ...prev, discount_code: e.target.value.toUpperCase() }))}
                  className="bg-[#0A0A0A] border-[#262626] rounded-none font-mono" placeholder="SUMMER25" data-testid="promo-code" />
              </div>
            </div>

            {form.discount_code && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label>Discount Type</Label>
                  <select value={form.discount_type} onChange={(e) => setForm(prev => ({ ...prev, discount_type: e.target.value }))}
                    className="w-full bg-[#0A0A0A] border border-[#262626] p-2 text-white" data-testid="promo-discount-type">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </div>
                <div>
                  <Label>Discount Value</Label>
                  <div className="relative">
                    {form.discount_type === 'percentage'
                      ? <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                      : <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />}
                    <Input type="number" value={form.discount_value} onChange={(e) => setForm(prev => ({ ...prev, discount_value: e.target.value }))}
                      className="bg-[#0A0A0A] border-[#262626] rounded-none pl-9" placeholder="25" data-testid="promo-discount-value" />
                  </div>
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                    className="bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="promo-end-date" />
                </div>
              </div>
            )}

            {/* Item Selection */}
            <div className="border-t border-[#262626] pt-4">
              <Label className="text-[#DAA520]">Select Items to Put on Sale</Label>
              <div className="mt-3 max-h-48 overflow-y-auto bg-[#0A0A0A] border border-[#262626] divide-y divide-[#1A1A1A]">
                {allItems.length === 0 ? (
                  <p className="p-4 text-[#666] text-sm text-center">No items available</p>
                ) : allItems.map(item => {
                  const isSelected = form.items.some(i => i.id === item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => !isSelected && addItem(item._type, item)}
                      disabled={isSelected}
                      className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${isSelected ? 'bg-[#1A1A1A] opacity-50' : 'hover:bg-[#1A1A1A]'}`}
                      data-testid={`select-item-${item.id}`}
                    >
                      {item.cover_image && <img src={item.cover_image} alt="" className="w-8 h-8 object-cover border border-[#262626] flex-shrink-0" />}
                      <span className="flex-1 text-sm truncate">{item.title || item.name}</span>
                      <span className="text-[10px] uppercase text-[#666] px-1.5 py-0.5 border border-[#333]">{item._type}</span>
                      <span className="text-sm font-mono text-[#A3A3A3]">${item.price?.toFixed(2)}</span>
                      {isSelected && <Check className="w-4 h-4 text-[#22C55E]" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected Items with Sale Prices */}
            {form.items.length > 0 && (
              <div className="space-y-2">
                <Label>Set Sale Prices</Label>
                {form.items.map((item, i) => (
                  <div key={`${item.id}-${i}`} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] p-3">
                    <span className="flex-1 text-sm truncate">{item.name}</span>
                    <span className="text-xs text-[#666]">${item.price?.toFixed(2)}</span>
                    <span className="text-[#666]">→</span>
                    <Input type="number" step="0.01" value={item.sale_price}
                      onChange={(e) => updateItemSalePrice(i, e.target.value)}
                      className="w-24 bg-[#141414] border-[#262626] rounded-none text-[#DAA520] font-mono h-8"
                      placeholder="Sale $" data-testid={`sale-price-${i}`} />
                    <button onClick={() => removeItem(i)} className="text-[#666] hover:text-[#FF3B30]" data-testid={`remove-item-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Promo Text */}
            <div className="border-t border-[#262626] pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label>Promo Text (for socials)</Label>
                <div className="flex gap-2">
                  <Button onClick={generatePromoText} disabled={generating} variant="outline" size="sm"
                    className="rounded-none border-[#262626] hover:border-[#DAA520] text-[#DAA520]" data-testid="ai-generate-btn">
                    {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    AI Generate
                  </Button>
                  {form.promo_text && (
                    <Button onClick={copyPromoText} variant="outline" size="sm"
                      className="rounded-none border-[#262626]" data-testid="copy-promo-text">
                      {copied ? <Check className="w-4 h-4 mr-1 text-[#22C55E]" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  )}
                </div>
              </div>
              <textarea
                value={form.promo_text}
                onChange={(e) => setForm(prev => ({ ...prev, promo_text: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#262626] p-3 text-white resize-none outline-none text-sm"
                rows={4} placeholder="Write or generate promo text..."
                data-testid="promo-text-area"
              />
            </div>

            {/* Promo Image */}
            <div className="border-t border-[#262626] pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Promo Image</Label>
                <Button onClick={downloadPromoImage} variant="outline" size="sm"
                  className="rounded-none border-[#DAA520] text-[#DAA520] hover:bg-[#DAA520] hover:text-black" data-testid="download-promo-img">
                  Download Image
                </Button>
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] p-4 flex justify-center">
                <canvas ref={canvasRef} className="max-w-full h-auto border border-[#262626]" style={{ maxHeight: 300 }} />
              </div>
              {form.promo_name && (
                <p className="text-[10px] text-[#666] mt-2 text-center">Preview updates when you click "Download Image"</p>
              )}
            </div>

            {/* Launch */}
            <Button onClick={handleSave} disabled={saving}
              className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none h-12 text-lg font-bold uppercase tracking-wider" data-testid="launch-promo-btn">
              {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> LAUNCHING...</> : 'LAUNCH PROMO'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
